import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API_URL, storePath } from "./api";

// AI Builder (sooquna-specs/suqoona-ai-builder-brief.txt). The admin never
// talks to the model: everything goes through the store's /ai routes.

export type AIStatus = {
  enabled: boolean;
  reason?: "no_key" | "turned_off";
  credits: AICredits;
  themes: { id: string; name: string; role: string }[];
  can_create_sections: boolean;
};

export type AICredits = {
  plan: string; monthly_cap: number; used_month: number; left_month: number;
  daily_cap: number; used_today: number; left_today: number; renews_at: string; enabled: boolean; confirm_above: number;
};

export type AISession = { id: string; theme_id: string | null; title: string; created_at: string; updated_at: string };

export type AIJob = {
  id: string; session_id: string; request: string; task_type: string; items: number;
  status: "running" | "waiting" | "queued" | "batch" | "finished" | "failed" | "cancelled" | "budget";
  summary: string; error?: string; credits: number; feedback: 1 | -1 | null; created_at: string; finished_at: string | null;
  changeset_id: string | null;
};

export type AIConversation = {
  session: AISession;
  messages: { role: "user" | "assistant"; text: string; job_id: string | null }[];
  jobs: AIJob[];
};

export type AIEstimate = { task_type: string; items: number; credits: number; needs_confirmation: boolean; background: boolean };

export type AITarget = { resource: string; id: string; field: string; locale?: string };
export type AIItem = {
  id: string; kind: "theme_file" | "new_file" | "content"; path?: string; target?: AITarget;
  label: string; before: unknown; after: unknown; accepted: boolean;
};
export type AIChangeset = {
  id: string; job_id: string; theme_id: string | null; status: "draft" | "applied" | "discarded" | "undone";
  applied_at: string | null; undone_at: string | null; summary: string[]; items: AIItem[];
};
export type AIConflict = { item_id: string; label: string; reason: string };
export type AISuggestion = { kind: string; count: number };

const p = (storeId: string, path: string) => storePath(storeId, "/ai" + path);

export const aiApi = (storeId: string) => ({
  status: () => api<AIStatus>(p(storeId, "/status")),
  credits: () => api<AICredits>(p(storeId, "/credits")),
  suggestions: () => api<{ data: AISuggestion[] }>(p(storeId, "/suggestions")).then((r) => r.data),
  sessions: () => api<{ data: AISession[] }>(p(storeId, "/sessions")).then((r) => r.data),
  newSession: (themeId: string | null) => api<AISession>(p(storeId, "/sessions"), { body: { theme_id: themeId } }),
  conversation: (id: string) => api<AIConversation>(p(storeId, `/sessions/${id}`)),
  send: (sessionId: string, body: { text: string; product_ids?: string[]; confirm?: boolean }) =>
    api<{ job?: AIJob; estimate?: AIEstimate }>(p(storeId, `/sessions/${sessionId}/messages`), { body }),
  stop: (jobId: string) => api<void>(p(storeId, `/jobs/${jobId}/stop`), { method: "POST" }),
  feedback: (jobId: string, rating: 1 | -1, comment = "") => api<void>(p(storeId, `/jobs/${jobId}/feedback`), { body: { rating, comment } }),
  changeset: (id: string) => api<AIChangeset>(p(storeId, `/changesets/${id}`)),
  review: (id: string, itemIds: string[], accepted: boolean) =>
    api<AIChangeset>(p(storeId, `/changesets/${id}/review`), { body: { item_ids: itemIds, accepted } }),
  apply: (id: string, resolve: Record<string, "ai" | "mine"> = {}) =>
    api<{ changeset: AIChangeset; conflicts?: AIConflict[] }>(p(storeId, `/changesets/${id}/apply`), { body: { resolve } }),
  discard: (id: string) => api<AIChangeset>(p(storeId, `/changesets/${id}/discard`), { method: "POST" }),
  undo: (id: string) => api<AIChangeset>(p(storeId, `/changesets/${id}/undo`), { method: "POST" }),
});

export type JobEvent =
  | { type: "step"; data: { tool?: string; step?: string } }
  | { type: "text" | "thinking"; data: { delta: string } }
  | { type: "status"; data: { status: string; changeset_id?: string; items?: number } }
  | { type: "changeset"; data: { changeset_id: string } }
  | { type: "question"; data: { question: string } }
  | { type: "done"; data: { status: string; summary: string; changeset_id: string | null; changes: string[]; message?: string } };

// useJobStream follows a job's progress (steps, streamed text, the result).
// Each connection takes a single-use ticket; on a drop it reconnects from
// the last event, so nothing is lost or shown twice.
export function useJobStream(storeId: string | undefined, jobId: string | null, onEvent: (e: JobEvent) => void) {
  const handler = useRef(onEvent);
  handler.current = onEvent;
  useEffect(() => {
    if (!storeId || !jobId) return;
    let closed = false;
    let es: EventSource | null = null;
    let lastId = "";
    let retry = 1000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const connect = async () => {
      if (closed) return;
      try {
        const { ticket } = await api<{ ticket: string }>(p(storeId, `/jobs/${jobId}/ticket`), { method: "POST" });
        if (closed) return;
        const q = new URLSearchParams({ ticket });
        if (lastId) q.set("last_event_id", lastId);
        es = new EventSource(`${API_URL}${p(storeId, `/jobs/${jobId}/stream`)}?${q}`);
        const dispatch = (ev: MessageEvent) => {
          lastId = ev.lastEventId || lastId;
          retry = 1000;
          try {
            handler.current({ type: ev.type, data: JSON.parse(ev.data) } as JobEvent);
          } catch {
            /* ignore malformed events */
          }
          if (ev.type === "done") { closed = true; es?.close(); }
        };
        for (const type of ["step", "text", "thinking", "status", "changeset", "question", "done"]) es.addEventListener(type, dispatch as EventListener);
        const reopen = () => {
          es?.close();
          es = null;
          if (closed) return;
          timer = setTimeout(connect, retry);
          retry = Math.min(retry * 2, 30000);
        };
        es.addEventListener("reconnect", () => { retry = 0; reopen(); });
        es.onerror = reopen;
      } catch {
        timer = setTimeout(connect, retry);
        retry = Math.min(retry * 2, 30000);
      }
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      es?.close();
    };
  }, [storeId, jobId]);
}

// useAIEnabled: the AI Builder's entry points show only where it's on for
// the store (API key set, rollout allow list, kill switches; all server side).
export function useAIEnabled(storeId: string | undefined) {
  const q = useQuery({ queryKey: ["ai", storeId, "status"], queryFn: () => aiApi(storeId!).status(), enabled: !!storeId, staleTime: 60_000, retry: false });
  return !!q.data?.enabled;
}
